import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { cn, pm } from 'lib/helpers/utility';

import { permissions as permissionsState } from 'components/state';

import List from './List';

const ModalSidebar = ({
  items,
  className,
  listClassName,
  contentClassName,
  activeKey,
  onActiveChange
}) => {
  const perms = useRecoilValue(permissionsState);
  const checkPerm = useCallback((p) => !p || pm(perms, p), [perms]);

  const computedDefault = useMemo(() => {
    const outer = [];
    for (const group of items || []) {
      if (!checkPerm(group?.permission)) continue;
      for (const child of group?.items || []) {
        if (child?.key && !child.hidden && checkPerm(child?.permission)) outer.push(child);
      }
    }
    return outer;
  }, [items, checkPerm]);

  const preferredKey = useMemo(() => {
    if (activeKey != null) {
      const hasKey = computedDefault?.some((ch) => String(ch?.key) === String(activeKey));
      if (hasKey) return String(activeKey);
    }
    return computedDefault?.[0]?.key;
  }, [computedDefault, activeKey]);

  const [internalKey, setInternalKey] = useState(preferredKey);

  useEffect(() => {
    if (preferredKey !== internalKey) setInternalKey(preferredKey);
  }, [preferredKey, internalKey]);

  const activeItem = useMemo(
    () => computedDefault?.find((ch) => String(ch?.key) === String(internalKey)),
    [computedDefault, internalKey]
  );

  const changeHandler = useCallback(
    (item) => {
      if (!item?.key) return;
      const nextKey = String(item.key);
      if (nextKey !== internalKey) {
        setInternalKey(nextKey);
        onActiveChange?.(nextKey);
      }
    },
    [internalKey, onActiveChange]
  );

  const content =
    typeof activeItem?.content === 'function' ? activeItem?.content() : activeItem?.content;

  return (
    <div className={cn('flex h-full w-full shrink-0 bg-white transition-all', className)}>
      <aside
        className={cn(
          'flex w-52 flex-col items-start justify-between overflow-y-auto border-0 border-r border-solid border-neutral-200',
          listClassName
        )}>
        <List items={items} onChange={changeHandler} activeItem={activeItem} />
      </aside>
      <div className={cn('flex flex-1 flex-col', contentClassName)}>{content}</div>
    </div>
  );
};

export default ModalSidebar;
